/**
 * XML Utility Functions
 *
 * Shared utilities for handling XML data from API responses.
 * Matches Angular implementation for consistency.
 */

/**
 * Removes XML declaration from a string.
 * Handles all XML declaration variations (different encodings, quotes, etc.)
 */
export const removeXmlDeclaration = (xml: string): string => {
  return xml.replace(/<\?xml[^?]*\?>/gi, '');
};

/**
 * Response item structure from FI data API
 */
export interface XmlDataItem {
  xmlData?: string;
  maskedAccNumber?: string;
  [key: string]: unknown;
}

/**
 * Extracts the data array from various API response formats.
 */
export const extractDataArray = (response: unknown): XmlDataItem[] | null => {
  if (!response || typeof response !== 'object') {
    return null;
  }

  const responseObj = response as Record<string, unknown>;
  const dataArray = (responseObj.data as Record<string, unknown>)?.data ?? responseObj.data;

  if (Array.isArray(dataArray)) {
    return dataArray;
  }

  return null;
};

/**
 * Extracts and combines XML payload from API response for combined download.
 * Matches Angular behavior:
 * - Removes XML declarations from each account
 * - Joins with empty string (not newline)
 * - Wraps in <Accounts> tag
 *
 * @param response - The API response
 * @returns Combined XML wrapped in <Accounts> tag
 */
export const extractCombinedXml = (response: unknown): string => {
  const dataArray = extractDataArray(response);

  if (dataArray && dataArray.length > 0) {
    const preparedXMLData = dataArray
      .map((item) => {
        if (typeof item?.xmlData === 'string') {
          // Remove XML declaration (matching Angular)
          return removeXmlDeclaration(item.xmlData);
        }
        return '';
      })
      .filter(Boolean);

    // Join with empty string and wrap in <Accounts> (matching Angular)
    return `<Accounts>${preparedXMLData.join('')}</Accounts>`;
  }

  // Check if response.data is a string (raw XML)
  if (response && typeof response === 'object') {
    const responseObj = response as Record<string, unknown>;
    if (typeof responseObj.data === 'string') {
      return responseObj.data;
    }
  }

  // Fallback
  return `<Accounts></Accounts>`;
};

/**
 * Legacy function for backward compatibility.
 * @deprecated Use extractCombinedXml instead
 */
export const extractXmlPayload = (response: unknown): string => {
  return extractCombinedXml(response);
};

/**
 * Legacy function for backward compatibility.
 * @deprecated Combined XML is now always wrapped properly by extractCombinedXml
 */
export const wrapXmlIfNeeded = (xmlPayload: string, rootElement = 'Accounts'): string => {
  const trimmed = xmlPayload.trim();
  if (trimmed.startsWith(`<${rootElement}>`)) {
    return xmlPayload;
  }
  return `<${rootElement}>${xmlPayload}</${rootElement}>`;
};
